import VueApollo from 'vue-apollo'
import { ApolloLink, concat } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'
import { setContext } from 'apollo-link-context'

const { createApolloClient, restartWebsockets } = require('vue-cli-plugin-apollo/graphql-client');

/****
 * {client name: http endpoint} mapping for supportin gmultiple clients
 * https://vue-apollo.netlify.com/guide/multiple-clients.html
 *
 * required export from the client:

  export const appnameApolloHttpLink = new HttpLink({
    uri: httpEndpoint
  })

 * Usage from the client:
  tags: {
    query: gql`...`,
    client: 'agency',
  }
 */

import { agencyApolloHttpLink } from '@bgov/agency-page-vue'
import { contractVehiclesApolloHttpLink } from '@bgov/contract-vehicles'
import { artemisHttpLink } from '@bgov/congressional-committee-vue'
import { opportunitySearchApolloHttpLink } from '@bgov/opportunities-vue';
import { myBgovApiHttpLink } from '@bgov/my-bgov-vue';

const httpEndpointMapping: {[key: string]: any} = {
  agency: agencyApolloHttpLink,
  gcCombosearch: agencyApolloHttpLink,
  contractVehicle: contractVehiclesApolloHttpLink,
  opportunitySearch: opportunitySearchApolloHttpLink,
  artemis: artemisHttpLink,
  myBgovApi: myBgovApiHttpLink,
}

// Name of the localStorage item
const AUTH_TOKEN = 'jwt'

/****
 * Default config options
 */
const defaultOptions = {
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  wsEndpoint: null,
  // LocalStorage token
  tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,

  // Override the way the Authorization header is set
  getAuth: (tokenName: string) => {
    if (typeof window !== 'undefined') {
      // get the authentication token from local storage if it exists
      const token = window.localStorage.getItem(tokenName)
      // return the headers to the context so httpLink can read them
      return token ? `Token token="${token}"` : ''
    }
  }
}

/****
 * generate jwtLink with refreshed jwt
 */

// Get the updated JWT token from the response header
const jwtLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response: any) => {
    const { response: { headers } } = operation.getContext()

    if (headers) {
      const refreshedJwt = headers.get('x-jwt')

      if (refreshedJwt) {
        localStorage.setItem(AUTH_TOKEN, refreshedJwt)
      }
    }
    return response
  })
})

/****
 * generate clients object for new VueApollo() options
  clients = {
    'clientName': apolloClient // with jwtLink and httEndpoint
  }
 */

const clients: {[key: string]: any} = {}

Object.keys(httpEndpointMapping).forEach((key) => {
  clients[key] = createApolloClient({
    ...defaultOptions,
    link: jwtLink.concat(httpEndpointMapping[key])
  }).apolloClient
})

// Call this in the Vue app file
export function createProvider(options = {}) {
  // Create default apollo client
  const { apolloClient, wsClient } = createApolloClient({
    ...defaultOptions,
    ...options,
  })
  apolloClient.wsClient = wsClient

  // Create vue apollo provider
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    clients,
    errorHandler(error: any) {
      // eslint-disable-next-line no-console
      console.error('%cError', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', error.message)
    },
  })

  return apolloProvider
}
